import { UseQueryResult, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  ResponseDtoMapDeviationWorkflowListDeviationQuestionUserResponseDto,
  DeviationQuestionUserResponseDto,
  DeviationQuestionnaireQuestionUserResponseDto,
} from 'src/generated/services/TFinancialApi';
import { tFinancialService } from 'src/infra/services/tFinancialService';
import { CacheKeys, cacheKeysUtil } from 'src/utils/cacheKeysUtil';
import { useInvoiceContext } from 'src/presentations/components/organisms/actionDetails/InvoiceContextProvider';

export interface UpdateArgs {
  deviationId: string;
  data: DeviationQuestionUserResponseDto[];
}

export interface UCTUpdateArgs {
  deviationId: string;
  data: DeviationQuestionnaireQuestionUserResponseDto;
}

export const useGetUserResponse = (deviationId: string): UseQueryResult<ResponseDtoMapDeviationWorkflowListDeviationQuestionUserResponseDto> =>
  useQuery(
    cacheKeysUtil.userResponse(deviationId),
    async (): Promise<ResponseDtoMapDeviationWorkflowListDeviationQuestionUserResponseDto> => (await tFinancialService.api.getUserResponse1(deviationId)).data,
    {
      enabled: !!deviationId,
    }
  );

export const useGetApprovalUserResponse = (
  shipmentInvoiceId: string,
  enabled: boolean = false
): UseQueryResult<ResponseDtoMapDeviationWorkflowListDeviationQuestionUserResponseDto> =>
  useQuery(
    cacheKeysUtil.approvalUserResponse(shipmentInvoiceId),
    async (): Promise<ResponseDtoMapDeviationWorkflowListDeviationQuestionUserResponseDto> =>
      (await tFinancialService.api.getUserResponse(shipmentInvoiceId)).data,
    {
      enabled,
    }
  );

export const useUpdateApprovalUserResponse = (shipmentInvoiceId: string, invoiceId: string) => {
  const queryClient = useQueryClient();
  return useMutation(async (data: DeviationQuestionUserResponseDto[]) => tFinancialService.api.postUserResponse(shipmentInvoiceId, data), {
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getShipment],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.invoiceSummary],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.approvalUserResponse],
        }),
        queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
      ]),
  });
};

export const useUpdateUserResponseDeviation = () => {
  const queryClient = useQueryClient();
  const { invoiceId } = useInvoiceContext();
  return useMutation(async (args: UCTUpdateArgs) => tFinancialService.api.postUserResponse1(args.deviationId, args.data), {
    onSuccess: () =>
      Promise.all([
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.getShipment],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.invoiceSummary],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.userResponse],
        }),
        queryClient.invalidateQueries({
          exact: false,
          queryKey: [CacheKeys.doubleChargeDetails],
        }),
        queryClient.invalidateQueries(cacheKeysUtil.getVersion(invoiceId)),
      ]),
  });
};
